import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Award, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard({ token }) {
  const [data, setData] = useState({ top_reps: [], top_managers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/leaderboard`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setData(res.data);
      } catch { toast.error("Failed to load"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [token]);

  if (loading) return <div className="text-center text-white/50 py-12">Loading...</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="leaderboard">
      {/* Top Sales Reps */}
      <div className="card p-6 border border-green-500/20">
        <div className="flex items-center gap-3 mb-6">
          <Award className="w-6 h-6 text-[#D4AF37]" />
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">Top Sales Reps</h3>
        </div>
        <div className="space-y-3" data-testid="top-reps">
          {data.top_reps.length === 0 ? (
            <p className="text-white/50 text-center py-4">No data yet</p>
          ) : (
            data.top_reps.map((rep, idx) => (
              <div 
                key={rep.id} 
                className="flex items-center justify-between p-3 bg-black/30 border border-white/5"
                data-testid={`rep-rank-${idx + 1}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 flex items-center justify-center font-bold ${
                    idx < 3 ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-white">{rep.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-[#D4AF37] font-bold">{rep.total_sales} sales</p>
                  <p className="text-green-400 text-sm">${(rep.total_commission || 0).toFixed(2)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Top Managers */}
      <div className="card p-6 border border-blue-500/20">
        <div className="flex items-center gap-3 mb-6">
          <Target className="w-6 h-6 text-[#00D4FF]" />
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">Top Managers</h3>
        </div>
        <div className="space-y-3" data-testid="top-managers">
          {data.top_managers.length === 0 ? (
            <p className="text-white/50 text-center py-4">No data yet</p>
          ) : (
            data.top_managers.map((mgr, idx) => (
              <div 
                key={mgr.id} 
                className="flex items-center justify-between p-3 bg-black/30 border border-white/5"
                data-testid={`manager-rank-${idx + 1}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 flex items-center justify-center font-bold ${
                    idx < 3 ? 'bg-[#00D4FF] text-black' : 'bg-white/10 text-white'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-white">{mgr.name}</span>
                </div>
                <p className="text-green-400 font-bold">${(mgr.total_commission || 0).toFixed(2)}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
